import React from 'react';

type BaseProps = {
  className?: string;
};

type CasinoLogoPlaceholderProps = BaseProps & {
  initials: string;
};

type GameImagePlaceholderProps = BaseProps & {
  title?: string;
};

type CasinoHeroPlaceholderProps = BaseProps & {
  casinoName: string;
};

export function CasinoLogoPlaceholder({ initials, className = '' }: CasinoLogoPlaceholderProps) {
  return (
    <div className={`relative flex h-full w-full items-center justify-center overflow-hidden rounded-[inherit] bg-[#0B0D19] ${className}`}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_22%_22%,rgba(100,181,246,0.22),transparent_45%),radial-gradient(circle_at_82%_18%,rgba(255,183,77,0.2),transparent_42%),radial-gradient(circle_at_48%_88%,rgba(168,85,247,0.18),transparent_45%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(140deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0)_42%)]" />
      <div className="relative flex h-11 w-11 items-center justify-center rounded-full border border-[#ffb74d]/40 bg-[#0f1424]/85 shadow-[0_0_20px_rgba(255,183,77,0.2)]">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="text-[#ffb74d]"
          aria-hidden="true"
        >
          <path
            d="M12 3.2 14.1 7.5 18.8 8.2 15.4 11.4 16.2 16 12 13.8 7.8 16l.8-4.6L5.2 8.2l4.7-.7L12 3.2Z"
            fill="currentColor"
          />
          <circle cx="12" cy="12" r="9.2" stroke="currentColor" strokeOpacity="0.55" />
        </svg>
      </div>
      <span className="sr-only">{initials}</span>
    </div>
  );
}

export function GameImagePlaceholder({ title = 'Game Demo', className = '' }: GameImagePlaceholderProps) {
  return (
    <div className={`relative h-full w-full overflow-hidden bg-[#0f111a] ${className}`}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(100,181,246,0.24),transparent_38%),radial-gradient(circle_at_80%_18%,rgba(255,183,77,0.24),transparent_38%),radial-gradient(circle_at_50%_88%,rgba(217,70,239,0.17),transparent_42%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(125deg,rgba(255,255,255,0.06)_0%,rgba(255,255,255,0)_48%)]" />
      <div className="absolute left-3 top-3 rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-200">
        Demo
      </div>
      <div className="absolute inset-x-3 bottom-3 rounded-lg border border-white/10 bg-[#121626]/70 px-3 py-2 text-[11px] font-medium text-slate-200">
        {title}
      </div>
    </div>
  );
}

export function CasinoHeroPlaceholder({ casinoName, className = '' }: CasinoHeroPlaceholderProps) {
  return (
    <div className={`absolute inset-0 overflow-hidden bg-[#0a0d18] ${className}`} aria-hidden>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_24%,rgba(100,181,246,0.25),transparent_40%),radial-gradient(circle_at_83%_16%,rgba(255,183,77,0.25),transparent_40%),radial-gradient(circle_at_50%_92%,rgba(168,85,247,0.18),transparent_44%)]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#15192B] via-[#15192B]/20 to-transparent" />
      <div className="absolute inset-y-0 left-1/2 w-2/5 min-w-[140px] -translate-x-1/2 border-x border-white/20 bg-[#0b0d19]/55">
        <div className="flex h-full w-full items-center justify-center">
          <div className="relative h-[82%] w-[82%] overflow-hidden rounded-xl border border-white/15 bg-[#12182c]/70 shadow-[0_12px_34px_rgba(0,0,0,0.35)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(100,181,246,0.26),transparent_38%),radial-gradient(circle_at_82%_18%,rgba(255,183,77,0.25),transparent_40%),radial-gradient(circle_at_50%_85%,rgba(168,85,247,0.18),transparent_44%)]" />
            <div className="absolute inset-0 bg-[linear-gradient(130deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0)_52%)]" />
            <div className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#ffb74d]/45 bg-[#0f1424]/90 shadow-[0_0_24px_rgba(255,183,77,0.2)]">
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-[#ffb74d]"
                aria-hidden="true"
              >
                <path
                  d="M12 3.2 14.1 7.5 18.8 8.2 15.4 11.4 16.2 16 12 13.8 7.8 16l.8-4.6L5.2 8.2l4.7-.7L12 3.2Z"
                  fill="currentColor"
                />
                <circle cx="12" cy="12" r="9.2" stroke="currentColor" strokeOpacity="0.55" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      <span className="sr-only">{casinoName}</span>
    </div>
  );
}
